import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { ThemeVersion } from "@/lib/api/types";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { BackButton } from "@/shared/layout/BackButton";
import { useThemeByIdQuery } from "../services/useThemeByIdQuery";
import { useCreateThemeVersionMutation } from "../services/useCreateThemeVersionMutation";
import {
  getLatestDraftVersion,
  sortVersionsDesc,
} from "../services/theme-versions";
import { ThemeEditForm } from "../components/ThemeEditForm";
import { ThemeStudio } from "../studio/ThemeStudio";
import {
  Button,
  LoadingSkeleton,
  ErrorState,
  SectionCard,
  useToast,
} from "@a2z/ui";

type ThemeDetailPageProps = {
  locale: Locale;
  themeId: string;
};

function VersionStateBadge({ version }: { version: ThemeVersion }) {
  const t = useTranslations();
  const isPublished = version.publishedAt !== null;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        isPublished
          ? "bg-[color:var(--success-100)] text-[color:var(--success-600)]"
          : "bg-[color:var(--warning-100)] text-[color:var(--warning-700)]"
      }`}
    >
      {isPublished ? t(keys.themes.published) : t(keys.themes.draft)}
    </span>
  );
}

export function ThemeDetailPage({ locale, themeId }: ThemeDetailPageProps) {
  const t = useTranslations();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useThemeByIdQuery({ themeId });
  const { mutateAsync: createVersion, isPending: isCreatingVersion } =
    useCreateThemeVersionMutation();
  const themeErrorStatus = getQueryErrorStatus(error);

  useEffect(() => {
    if (themeErrorStatus === 404) {
      navigate(`/${locale}/admin/themes`, { replace: true });
    }
  }, [locale, navigate, themeErrorStatus]);

  if (isLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (themeErrorStatus === 404) {
    return null;
  }

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const versions = sortVersionsDesc(data.versions);
  const latestDraft = getLatestDraftVersion(data.versions);

  const handleCreateVersion = async () => {
    try {
      await createVersion({ themeId: data.id });
      toast.success(t(keys.common.updateSuccess));
    } catch {
      toast.error(t(keys.errors.generic));
    }
  };

  return (
    // Wide, not the usual `max-w-3xl` detail width: the studio is a
    // three-pane editor and squashing it into a reading column would collapse
    // the preview to uselessness. The metadata card keeps the narrow width.
    //
    // `data-full-bleed` additionally releases the AppShell's `max-w-6xl`
    // content cap (see the `.admin-main > div:has([data-full-bleed])` rule in
    // app/styles.css) — `w-full` alone only fills that 1152px column, which
    // left the studio inset with dead space either side.
    <section
      data-full-bleed
      className="flex w-full flex-col gap-5 lg:px-6 lg:py-6"
    >
      <BackButton
        label={t(keys.common.back)}
        fallbackHref={`/${locale}/admin/themes`}
      />

      <SectionCard
        title={t(keys.themes.metadataTitle)}
        right={
          <Button
            type="button"
            variant="outline"
            // Only one open draft at a time — a second would orphan this one
            // (the backend also enforces this). Edit/publish the draft first.
            disabled={isCreatingVersion || Boolean(latestDraft)}
            onClick={handleCreateVersion}
            className="h-9 rounded-full px-3 text-xs font-semibold"
          >
            {t(keys.themes.newVersion)}
          </Button>
        }
      >
        {/* The metadata form is NOT rendered here — it is handed to the studio
            and revealed by a toolbar toggle beside Desktop/Tablet/Mobile. It is
            set once at creation and rarely revisited, so it was costing the
            top of every page for something read almost never. */}
        <div>
          <div className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">
            {t(keys.themes.versionsTitle)}
          </div>
          <div className="grid gap-3">
            {versions.length === 0 ? (
              <div className="text-sm text-[color:var(--text-muted)]">
                {t(keys.common.empty)}
              </div>
            ) : (
              versions.map((version) => (
                <div
                  key={version.id}
                  className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[color:var(--text-primary)]">
                        {t(keys.themes.versionLabel, {
                          version: version.version,
                        })}
                      </span>
                      <VersionStateBadge version={version} />
                    </div>
                    {version.publishedAt ? (
                      <span className="text-xs text-[color:var(--text-muted)]">
                        {t(keys.themes.publishedAt)}: {version.publishedAt}
                      </span>
                    ) : null}
                  </div>
                  {version.thumbnailUrl ? (
                    <img
                      src={version.thumbnailUrl}
                      alt={t(keys.themes.thumbnailPreviewAlt)}
                      className="mt-3 max-h-32 w-auto rounded-lg border border-[color:var(--border-light)] object-contain"
                    />
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>

        {latestDraft ? (
          <div className="mt-5 border-t border-[color:var(--border-light)] pt-5">
            <div className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">
              {t(keys.themes.versionLabel, { version: latestDraft.version })}
            </div>
            <ThemeStudio
              themeId={data.id}
              version={latestDraft}
              locale={locale === "ar" ? "ar" : "en"}
              detailsPanel={<ThemeEditForm theme={data} />}
            />
          </div>
        ) : versions[0] ? (
          // No draft, but the theme has a published version — show the studio in
          // READ-ONLY mode so selecting a theme reveals its sections + preview
          // (a published version is immutable, so no Save/Publish; the toolbar's
          // New-version button branches a draft to edit). The metadata form
          // stays reachable via the studio's details toggle. Before this, a
          // published-only theme showed only the metadata form and the sections
          // were invisible until someone created a draft.
          <div className="mt-5 border-t border-[color:var(--border-light)] pt-5">
            <div className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">
              {t(keys.themes.versionLabel, { version: versions[0].version })}
            </div>
            <ThemeStudio
              themeId={data.id}
              version={versions[0]}
              locale={locale === "ar" ? "ar" : "en"}
              detailsPanel={<ThemeEditForm theme={data} />}
              readOnly
              onCreateVersion={handleCreateVersion}
              isCreatingVersion={isCreatingVersion}
            />
          </div>
        ) : (
          // Truly no versions at all — keep the metadata form as the only editor.
          <div className="mt-4 flex flex-col gap-5">
            <div className="text-xs text-[color:var(--text-muted)]">
              {t(keys.themes.noDraftHint)}
            </div>
            <div className="w-full max-w-3xl border-t border-[color:var(--border-light)] pt-5">
              <div className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">
                {t(keys.themes.metadataTitle)}
              </div>
              <ThemeEditForm theme={data} />
            </div>
          </div>
        )}
      </SectionCard>
    </section>
  );
}
